import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DateTime } from "luxon";

import { unavailableReasonProps } from "/imports/libs/unavailableReason";
import { DateSelector, EmptyState, LoadingSpinner, PickerRulesPanel, ServiceAvailabilityNotice } from "/imports/ui/components/shared";
import { TimeSlotCard, SLOT_SECTION_LABEL_SX, CARD_SX, getUniformGridPrice, PackComposerTray } from "/imports/ui/components/booking";
import DurationPrompt from "./components/DurationPrompt";
import CourtFilterSidebar from "./components/CourtFilterSidebar";
import CourtWeekGrid from "./components/CourtWeekGrid";
import CourtOptionsPopover from "./components/CourtOptionsPopover";
import CourtRescheduleBanner from "./components/CourtRescheduleBanner";
import CourtRescheduleDialog from "./components/CourtRescheduleDialog";
import { rescheduleEmptyProps } from "./courtHelpers";

// Desktop layout — sticky filter sidebar + a results pane that toggles between
// Day (one day's bucketed slot cards) and Week (a 7-day grid: days as columns,
// time-of-day buckets as rows). Multi-option times open a popover in both modes.
function CourtsDesktopLayout({ court }) {
  const {
    navigate,
    slug,
    currency,
    filters,
    setFilter,
    setSelectedSlot,
    loading,
    error,
    availability,
    viewMode,
    setViewMode,
    weekStart,
    weekDates,
    weekGrouped,
    maxBookableDate,
    canGoNextWeek,
    suggestedGroups,
    bucketedSlots,
    handleWeekChipClick,
    getOptionsLabel,
    goPrevWeek,
    goNextWeek
  } = court;

  const weekHasAnySlots = weekDates.some((d) => (weekGrouped.get(d) || []).length > 0);
  // A dated closure / time off emptied the day: say so, and offer the lever
  // the client actually has (drop the resource filter) rather than the date.
  const closureProps = unavailableReasonProps({
    closureReasons: availability?.closureReasons,
    canClearResource: !!filters.facilityId && court.allowCourtPick !== false,
    onClearResource: () => {
      setFilter("facilityId", null);
      setSelectedSlot(null);
    },
    onNextDate: () => {
      setFilter("date", court.tomorrowISO);
      setSelectedSlot(null);
    }
  });

  const availabilityInfo = availability?.availabilityInfo || [];
  const weekLabel = `${DateTime.fromISO(weekDates[0]).toFormat("MMM d")} – ${DateTime.fromISO(weekDates[6]).toFormat("MMM d, yyyy")}`;
  const canGoPrevWeek = weekStart > court.thisWeekStart;

  // One shared price across the whole day → drop it from every cell (the
  // duration picker already states it); varying prices stay per-cell.
  const uniformDayPrice = getUniformGridPrice(bucketedSlots.flatMap((b) => b.groups));

  const packMode = court.pack?.active;
  const isRescheduling = court.reschedule?.active;

  const renderDaySlotCard = (group) => {
    const isMulti = group.slots.length > 1;
    return (
      <TimeSlotCard
        key={group.time}
        time={group.time}
        price={group.minPrice}
        currency={group.currency || currency}
        selected={packMode && court.pack.isPicked(filters.date, group.time)}
        onClick={(e) => handleWeekChipClick(e, group, filters.date)}
        optionsLabel={getOptionsLabel(group.slots)}
        pricePrefix={isMulti ? "from" : null}
        hidePrice={packMode || uniformDayPrice != null}
        dense
        align="left"
      />
    );
  };

  return (
    <Box sx={{ pb: packMode ? { xs: "11rem", md: "7rem" } : 0 }}>
      {/* Title */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
        <IconButton size="small" onClick={court.goBack} aria-label="Go back" sx={{ ml: -0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ fontSize: "1.75rem", fontWeight: 800, letterSpacing: "-0.02em" }}>
          {packMode ? "Pick your pack sessions" : isRescheduling ? "Move your court booking" : "Reserve Your Court"}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {packMode
          ? "Tap the times you want — the week view shows the whole pattern at once. Your picks stay as you move between days."
          : isRescheduling
            ? "Pick the new time. Same court booking, same length."
            : "Set your filters, then pick a time."}
      </Typography>

      {isRescheduling && <CourtRescheduleBanner reschedule={court.reschedule} currency={currency} sx={{ mb: 3, maxWidth: "48rem" }} />}

      {/* The club's rules for buying a pack, in the one place the client can still
          act on them. Only while composing a pack — an ordinary booking has no
          pack rules to obey. */}
      {packMode && <PickerRulesPanel title="Before you buy" rules={court.pack.packRules} sx={{ mb: 3 }} />}

      <Box sx={{ display: "flex", gap: 4, alignItems: "flex-start" }}>
        <CourtFilterSidebar court={court} />

        {/* Results pane */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Sticky header: view toggle + (day date strip / week navigation). */}
          <Box sx={{ position: "sticky", top: 64, zIndex: 4, bgcolor: "background.default", pt: 0.5, pb: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: viewMode === "day" ? 2 : 0,
                gap: 2,
                flexWrap: "wrap"
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  p: 0.4,
                  borderRadius: "9999px",
                  border: "1.5px solid",
                  borderColor: "divider",
                  bgcolor: "#fff",
                  gap: 0.4
                }}
              >
                {["day", "week"].map((m) => {
                  const active = viewMode === m;
                  return (
                    <ButtonBase
                      key={m}
                      onClick={() => setViewMode(m)}
                      sx={{ px: 2, py: 0.55, borderRadius: "9999px", bgcolor: active ? "primary.main" : "transparent", transition: "all 0.15s ease" }}
                    >
                      <Typography
                        sx={{ fontSize: "0.8rem", fontWeight: 600, textTransform: "capitalize", color: active ? "#fff" : "text.secondary" }}
                      >
                        {m}
                      </Typography>
                    </ButtonBase>
                  );
                })}
              </Box>

              {viewMode === "week" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 700, letterSpacing: "-0.01em" }}>{weekLabel}</Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={goPrevWeek}
                      disabled={!canGoPrevWeek}
                      sx={{ border: "1px solid", borderColor: "divider", borderRadius: "50%", width: "2.125rem", height: "2.125rem" }}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={goNextWeek}
                      disabled={!canGoNextWeek}
                      sx={{ border: "1px solid", borderColor: "divider", borderRadius: "50%", width: "2.125rem", height: "2.125rem" }}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Box>

            {viewMode === "day" && (
              <Paper elevation={0} sx={{ ...CARD_SX, p: 2 }}>
                <DateSelector
                  value={filters.date}
                  onChange={(date) => {
                    setFilter("date", date);
                    setSelectedSlot(null);
                  }}
                  daysToShow={7}
                  maxDate={maxBookableDate}
                  today={court.todayISO}
                  dense
                />
              </Paper>
            )}
          </Box>

          {!filters.duration ? (
            <DurationPrompt />
          ) : viewMode === "day" ? (
            <>
              {loading ? (
                <LoadingSpinner message="Loading availability..." />
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : bucketedSlots.length > 0 ? (
                <>
                  {suggestedGroups.length > 0 && (
                    <Box sx={{ mb: 3.5 }}>
                      <Typography sx={{ ...SLOT_SECTION_LABEL_SX, mb: 1.5 }}>Suggested for you · {suggestedGroups.length}</Typography>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(7.75rem, 1fr))", gap: 1.25 }}>
                        {suggestedGroups.map(renderDaySlotCard)}
                      </Box>
                    </Box>
                  )}
                  {bucketedSlots.map((bucket) => (
                    <Box key={bucket.id} sx={{ mb: 3.5 }}>
                      <Typography sx={{ ...SLOT_SECTION_LABEL_SX, mb: 1.5 }}>
                        {bucket.label} · {bucket.groups.length}
                      </Typography>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(7.75rem, 1fr))", gap: 1.25 }}>
                        {bucket.groups.map(renderDaySlotCard)}
                      </Box>
                    </Box>
                  ))}
                </>
              ) : closureProps ? (
                <EmptyState {...closureProps} />
              ) : availabilityInfo.length > 0 ? (
                <ServiceAvailabilityNotice
                  availabilityInfo={availabilityInfo}
                  actionLabel="Try Tomorrow"
                  onAction={() => {
                    setFilter("date", court.tomorrowISO);
                    setSelectedSlot(null);
                  }}
                />
              ) : isRescheduling ? (
                <EmptyState
                  {...rescheduleEmptyProps({
                    onNext: () => {
                      setFilter("date", court.tomorrowISO);
                      setSelectedSlot(null);
                    }
                  })}
                />
              ) : (
                <EmptyState
                  title="No availability"
                  description="No courts are available for the selected date and filters. Try a different date or adjust your filters."
                  actionLabel="Try Tomorrow"
                  onAction={() => {
                    setFilter("date", court.tomorrowISO);
                    setSelectedSlot(null);
                  }}
                />
              )}
            </>
          ) : loading ? (
            <LoadingSpinner message="Loading availability..." />
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : !weekHasAnySlots ? (
            availabilityInfo.length > 0 ? (
              <ServiceAvailabilityNotice availabilityInfo={availabilityInfo} actionLabel="Next week" onAction={goNextWeek} />
            ) : isRescheduling ? (
              <EmptyState {...rescheduleEmptyProps({ week: true, onNext: goNextWeek })} />
            ) : (
              <EmptyState
                title="No availability this week"
                description="No courts are available for the selected filters this week. Try the next week or adjust your filters."
                actionLabel="Next week"
                onAction={goNextWeek}
              />
            )
          ) : (
            <CourtWeekGrid court={court} />
          )}
        </Box>
      </Box>

      <CourtOptionsPopover court={court} />
      <PackComposerTray pack={court.pack} />
      {isRescheduling && <CourtRescheduleDialog reschedule={court.reschedule} currency={currency} isMobile={false} />}
    </Box>
  );
}

export default CourtsDesktopLayout;
